/**
 * Backend resolutions API — typed TanStack Query hooks.
 *
 * Wraps `GET /v1/resolutions` (list) and `GET /v1/resolutions/{id}` (detail).
 * Response shapes mirror the backend handlers in
 * `backend/src/handlers/resolutions-list.ts` and `resolutions-get.ts`.
 *
 * The signing mutation continues to live in `components/screens/resolution-sign.tsx`
 * for now — it carries enough screen-local state that hoisting it adds
 * indirection without payoff. It will move here when a second signing
 * surface (mobile parity, AES handoff resume) calls it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';

export type ResolutionStatus = 'draft' | 'awaiting' | 'completed' | 'cancelled';

/** Shape returned by the list endpoint per row (no `body` — detail-only). */
export interface ResolutionSummary {
  id: string;
  trustId: string;
  trustName: string | null;
  ref: string;
  title: string;
  category: string;
  status: ResolutionStatus;
  signers_signed: number;
  signers_total: number;
  signerUserIds: string[];
  amount?: number;
  deadline?: string;
  canonicalHash: string;
  youAreSigner: boolean;
  /** Present iff `youAreSigner === true`. */
  youHaveSigned?: boolean;
  /** WS-4 register backfill: `'imported-document'` marks a resolution
   *  auto-created from a client's uploaded historical document (signed
   *  off-system) rather than created + e-signed in-app. `'app'`/absent = the
   *  live workflow. Drives the "Imported" badge on the resolutions register. */
  source?: 'app' | 'imported-document';
  importedFromDocumentId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ResolutionsListResponse {
  resolutions: ResolutionSummary[];
  correlationId: string;
}

export interface SignatureSummary {
  id: string;
  userId: string;
  userDisplayName: string;
  signedAt: string;
  signatureMethod: 'simple-electronic' | 'advanced-electronic' | 'qualified-electronic';
  /** Decision #2 — the capacity the signer signed in (e.g. "Independent
   *  Trustee", "Trustee", "Beneficiary", "External counsel"), server-derived
   *  at sign-time. Absent when the signer held no roster entry. */
  signingCapacity?: string;
}

export interface ResolutionDetail {
  id: string;
  trustId: string;
  ref: string;
  title: string;
  category: string;
  status: ResolutionStatus;
  signers_signed: number;
  signers_total: number;
  signerUserIds: string[];
  amount?: number;
  deadline?: string;
  body: string;
  canonicalHash: string;
  createdAt: string;
  updatedAt: string;
  /** COMBO-003 — set when an external signer declined. PII-free (role + count
   *  + time). The detail screen shows an "action required" banner so a
   *  declined resolution is not silently stuck in 'awaiting'. */
  lastDeclinedAt?: string;
  lastDeclinedByRole?: string;
  declinedCount?: number;
  /** Provenance. `'imported-document'` = signed ON PAPER outside Elreign and
   *  filed here as a record (WS-4 ingestion backfill, or the "upload a
   *  previously signed resolution" path). The detail screen keys its honest
   *  copy off this: without it, a `completed` imported record would be told
   *  "Fully signed and recorded on the audit chain", which is untrue. */
  source?: 'app' | 'imported-document';
  /** The vault document an imported record was filed from. */
  importedFromDocumentId?: string;
  /** The date on the paper, as captured/extracted (free-text). */
  importedExecutionDate?: string;
}

export interface ResolutionGetResponse {
  resolution: ResolutionDetail;
  trustName: string | null;
  signatures: SignatureSummary[];
  youAreSigner: boolean;
  youHaveSigned: boolean;
  correlationId: string;
}

export interface UseResolutionsOptions {
  trustId?: string;
  status?: ResolutionStatus;
  awaitingMySignature?: boolean;
  limit?: number;
  /** Defaults to true; set false to defer the network call. */
  enabled?: boolean;
}

function buildListPath(opts: UseResolutionsOptions): string {
  const search = new URLSearchParams();
  if (opts.trustId) search.set('trustId', opts.trustId);
  if (opts.status) search.set('status', opts.status);
  if (opts.awaitingMySignature) search.set('awaitingMySignature', 'true');
  if (opts.limit !== undefined) search.set('limit', String(opts.limit));
  const qs = search.toString();
  return qs ? `/api/v1/resolutions?${qs}` : '/api/v1/resolutions';
}

export function useResolutions(opts: UseResolutionsOptions = {}) {
  return useQuery<ResolutionsListResponse>({
    // Stable key — the spread of filter fields makes cache hits precise.
    queryKey: ['resolutions', 'list', {
      trustId: opts.trustId ?? null,
      status: opts.status ?? null,
      awaitingMySignature: !!opts.awaitingMySignature,
      limit: opts.limit ?? null,
    }],
    queryFn: ({ signal }) =>
      api<ResolutionsListResponse>(buildListPath(opts), { signal }),
    enabled: opts.enabled ?? true,
  });
}

// ── Create resolution ──────────────────────────────────────────────────────
// POST /v1/resolutions. Step-up gated server-side; the StepUpInterceptor at
// __root.tsx catches the 403 and redirects to /auth/refresh-step-up.

export interface CreateResolutionRequest {
  trustId: string;
  title: string;
  /** Free-text category — defaults to 'general' if omitted by the caller. */
  category?: string;
  /** Signer user-ids. The creator is always required; the backend rejects
   *  empty arrays. Defaults to [currentUser] when omitted. */
  signerUserIds?: string[];
  /** Free-text body of the resolution. Defaults to a minimal placeholder
   *  when omitted so the draft can be edited later. */
  body?: string;
  /** 'draft' (default) or 'awaiting' when circulating immediately. */
  status?: 'draft' | 'awaiting';
  ref?: string;
  amount?: number;
  deadline?: string;
}

export interface CreateResolutionResponse {
  resolution: ResolutionDetail;
  correlationId: string;
}

export function useCreateResolution() {
  const qc = useQueryClient();
  return useMutation<CreateResolutionResponse, ApiError, CreateResolutionRequest>({
    mutationFn: (body) =>
      api<CreateResolutionResponse>('/api/v1/resolutions', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['resolutions'] });
      void qc.invalidateQueries({ queryKey: ['trusts', 'resolutions'] });
    },
  });
}

export function useResolution(resolutionId: string | undefined) {
  return useQuery<ResolutionGetResponse>({
    queryKey: ['resolutions', 'detail', resolutionId ?? null],
    queryFn: ({ signal }) =>
      api<ResolutionGetResponse>(
        `/api/v1/resolutions/${encodeURIComponent(resolutionId!)}`,
        { signal },
      ),
    enabled: !!resolutionId,
  });
}

// ── Import a previously signed (paper) resolution ───────────────────────────
//
// POST /v1/trusts/{trustId}/resolutions/import. The counterpart to
// `useCreateResolution`: instead of drafting something Elreign will witness
// being signed, this files a resolution that was ALREADY executed on paper,
// outside Elreign, as an existing record on the register.
//
// The bytes go up the normal vault path first (`useDocumentUploadInit` →
// `putBytesToSas` → `useDocumentUploadFinalize`); this call mints the register
// row that points at the resulting document. The backend stamps
// `source: 'imported-document'` + a terminal `completed` status with ZERO
// signers, writes NO signature event, and sends NO signing request — so an
// uploaded record can never be mistaken for one Elreign witnessed.

export interface ImportResolutionRequest {
  trustId: string;
  /** The vault document holding the signed paper resolution — must already be
   *  filed to `trustId` (the backend 404s otherwise). */
  documentId: string;
  title: string;
  /** A `lib/resolution-categories.ts` slug. Omit to store `imported`. */
  category?: string;
  /** The date on the paper, free-text (a 2014 scan rarely has a clean ISO date). */
  executionDate?: string;
  /** Optional operator note — what the resolution decided, in their words. */
  note?: string;
}

export interface ImportResolutionResponse {
  resolution: ResolutionDetail;
  correlationId: string;
}

export function useImportHistoricalResolution() {
  const qc = useQueryClient();
  return useMutation<ImportResolutionResponse, ApiError, ImportResolutionRequest>({
    mutationFn: ({ trustId, ...body }) =>
      api<ImportResolutionResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/resolutions/import`,
        { method: 'POST', body },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['resolutions'] });
      void qc.invalidateQueries({ queryKey: ['trusts', 'resolutions'] });
      // The source document's vault row is now linked to a register record —
      // refresh it so the register's "documents on file" section stops
      // double-listing it.
      void qc.invalidateQueries({ queryKey: ['trusts', 'documents', vars.trustId] });
    },
  });
}

// ── Patch resolution (edit / circulate / cancel) ────────────────────────────
// PATCH /v1/resolutions/{id}. Used by the detail screen to circulate a draft
// (status → 'awaiting'). Step-up gated server-side; StepUpInterceptor handles
// the 403 redirect.

export interface PatchResolutionInput {
  resolutionId: string;
  patch: {
    status?: 'draft' | 'awaiting' | 'cancelled';
    title?: string;
    body?: string;
    amount?: number;
    deadline?: string;
    signerUserIds?: string[];
  };
}

export interface PatchResolutionResponse extends CreateResolutionResponse {
  /** True when the PATCH asked for what the resolution already holds (a
   *  resent request, a stale second tab): nothing was written, recorded or
   *  sent, and `resolution` is the resolution as it stands. The screen must
   *  say so rather than report the change it asked for. */
  unchanged?: boolean;
}

export function usePatchResolution() {
  const qc = useQueryClient();
  return useMutation<PatchResolutionResponse, ApiError, PatchResolutionInput>({
    mutationFn: ({ resolutionId, patch }) =>
      api<PatchResolutionResponse>(
        `/api/v1/resolutions/${encodeURIComponent(resolutionId)}`,
        { method: 'PATCH', body: patch },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['resolutions'] });
      void qc.invalidateQueries({ queryKey: ['resolutions', 'detail', vars.resolutionId] });
      void qc.invalidateQueries({ queryKey: ['trusts', 'resolutions'] });
    },
  });
}

// ── Bulk delete (multi-select) ──────────────────────────────────────────────
// POST /v1/trusts/{trustId}/resolutions/bulk-delete. Soft-deletes many
// draft/awaiting/cancelled resolutions in one call, reusing the SAME tombstone
// + authz as the single delete. Returns an honest per-id outcome array so the
// UI can report a partial result ("3 deleted, 1 could not be removed"). No
// step-up (the single delete carries none either).

/** Outcome for one requested id — mirrors the backend `BulkDeleteOutcome`. */
export type BulkDeleteOutcome = 'deleted' | 'not-found-or-completed';

export interface BulkDeleteResult {
  resolutionId: string;
  outcome: BulkDeleteOutcome;
}

export interface BulkDeleteResolutionsResponse {
  results: BulkDeleteResult[];
  deletedCount: number;
  correlationId: string;
}

export interface BulkDeleteResolutionsInput {
  trustId: string;
  resolutionIds: string[];
}

export function useBulkDeleteResolutions() {
  const qc = useQueryClient();
  return useMutation<
    BulkDeleteResolutionsResponse,
    ApiError,
    BulkDeleteResolutionsInput
  >({
    mutationFn: ({ trustId, resolutionIds }) =>
      api<BulkDeleteResolutionsResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/resolutions/bulk-delete`,
        { method: 'POST', body: { resolutionIds } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['resolutions'] });
      void qc.invalidateQueries({ queryKey: ['trusts', 'resolutions'] });
    },
  });
}

// ── External signers (invite + list) ────────────────────────────────────────
// The creator-side counterpart to the public `/sign/external/$token` recipient
// flow. Inviting a NON-app party (external trustee, beneficiary, outside
// counsel) registers them as a REQUIRED signer on the resolution and emails
// them a single-use signing link. Mirrors the backend handlers in
// `backend/src/handlers/resolutions-external-signer-create.ts` (POST) and
// `resolutions-external-signers-list.ts` (GET).

/** Roles an external (non-app) signer can be invited under. */
export type ExternalSignerRole = 'external-trustee' | 'beneficiary' | 'external-counsel';

/** Derived lifecycle of an external signer's invitation. */
export type ExternalSignerStatus = 'pending' | 'signed' | 'declined' | 'expired';

/** One row in the external-signers list (GET). Email is masked server-side. */
export interface ExternalSignerView {
  recipientUserId: string;
  name: string;
  emailMasked: string;
  role: string;
  status: ExternalSignerStatus;
  invitedAt: string;
  expiresAt: string;
}

export interface ResolutionExternalSignersResponse {
  externalSigners: ExternalSignerView[];
  correlationId: string;
}

/** GET /v1/resolutions/{id}/external-signers. Powers the detail screen's
 *  "External signers" panel. Read-only, not step-up gated. */
export function useResolutionExternalSigners(resolutionId: string | undefined) {
  return useQuery<ResolutionExternalSignersResponse>({
    queryKey: ['resolutions', resolutionId ?? null, 'external-signers'],
    queryFn: ({ signal }) =>
      api<ResolutionExternalSignersResponse>(
        `/api/v1/resolutions/${encodeURIComponent(resolutionId!)}/external-signers`,
        { signal },
      ),
    enabled: !!resolutionId,
  });
}

export interface CreateExternalSigningInvitationRequest {
  name: string;
  email: string;
  /** E.164, e.g. +27821234567. Optional — SMS OTP channel only when present. */
  phoneE164?: string;
  role: ExternalSignerRole;
}

/** Recipient summary echoed back on a successful invite (201). */
export interface ExternalSignerRecipient {
  recipientUserId: string;
  name: string;
  emailMasked: string;
  role: ExternalSignerRole;
  status: 'pending';
  expiresAt: string;
}

export interface CreateExternalSigningInvitationResponse {
  recipient: ExternalSignerRecipient;
  /** New quorum size after the invitee was added as a required signer. */
  signersTotal: number;
  /** True when this call re-issued an existing invite (fresh link, previous
   *  one revoked) rather than creating the first invitation. Older deploys
   *  omit the field. */
  resent?: boolean;
  /** DELIVERY HONESTY (E2E 2026-08-21): false when the invite row was created
   *  but no email actually left the building (no provider configured on the
   *  environment). Older deploys omit the field — treat missing as delivered. */
  delivered?: boolean;
  /** Human-readable next step for the firm when `delivered` is false. */
  deliveryNote?: string;
  correlationId: string;
}

/** POST /v1/resolutions/{id}/external-signers. Step-up gated server-side; the
 *  StepUpInterceptor catches the 403 and replays. On success, refresh both the
 *  external-signers panel and the resolution detail (signers_total changed). */
export function useCreateExternalSigningInvitation(resolutionId: string) {
  const qc = useQueryClient();
  return useMutation<
    CreateExternalSigningInvitationResponse,
    ApiError,
    CreateExternalSigningInvitationRequest
  >({
    mutationFn: (body) =>
      api<CreateExternalSigningInvitationResponse>(
        `/api/v1/resolutions/${encodeURIComponent(resolutionId)}/external-signers`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['resolutions', resolutionId, 'external-signers'] });
      void qc.invalidateQueries({ queryKey: ['resolutions'] });
    },
  });
}
